import {
  getTeamMemberEmails,
  submitFreshdeskTicket,
  appendFreshdeskTicketDefaults,
} from "common/freshdesk";
import Constants from "common/constants";
import {
  isManagingBrokerNotListed,
  isOfficeNotListed,
  MANAGING_BROKER_NOT_LISTED_LABEL,
  OFFICE_NOT_LISTED_LABEL,
} from "./brokerage";

export const createBrokerageFreshdeskTicket = async ({
  agentData,
  subject,
  description,
}) => {
  const formDataToSend = new FormData();
  const teamMemberEmails = getTeamMemberEmails(agentData);
  const fullName = `${agentData.listgenUserFirstName || ""} ${agentData.listgenUserLastName || ""}`.trim();

  formDataToSend.append("name", fullName);
  formDataToSend.append("email", agentData.emailAddress || "");
  formDataToSend.append("subject", subject);
  formDataToSend.append("description", description);

  teamMemberEmails.forEach((email) => {
    formDataToSend.append("cc_emails[]", email);
  });

  appendFreshdeskTicketDefaults(formDataToSend);

  return submitFreshdeskTicket(formDataToSend);
};

export const buildFreshdeskDescription = (
  agentData,
  values,
  ticketType,
  screenName
) => {
  const fullName =
    `${agentData.listgenUserFirstName || ""} ${agentData.listgenUserLastName || ""}`.trim() ||
    "-";
  const lines = [
    `Agent: ${fullName}`,
    `Email: ${agentData.emailAddress || "-"}`,
    `ListGen User ID: ${agentData.listgenUserID || "-"}`,
    `Brokerage: ${values.brokerageName || agentData.brokerageName || "-"}`,
    `County ID: ${agentData.countyID || "-"}`,
    `Screen: ${screenName || "-"}`,
    `Ticket Type: ${ticketType}`,
  ];

  return lines.map((line) => `<div>${line}</div>`).join("");
};

export const OFFICE_NOT_LISTED_TICKET_SUBJECT =
  "Office location not listed - Agent Request";
export const MANAGING_BROKER_NOT_LISTED_TICKET_SUBJECT =
  "Managing broker not listed - Agent Request";
export const NEW_AGENT_REGISTERED_TICKET_TYPE = "New Agent Registered";

const buildNewAgentRegisteredDescription = (fullName, countyName) => {
  const lines = [
    `Agent: ${fullName}`,
    `County: ${countyName || "-"}`,
    `Please start agent ListGen training as soon as possible.`,
  ];

  return lines.map((line) => `<div>${line}</div>`).join("");
};

// Internal notification only, created after a new agent successfully
// completes registration - not tied to any office/broker-not-listed
// condition, and never a precondition for registration succeeding (see
// RegisterAgent's completeAgentRegistration, which fires this only after
// the registration API call itself has already succeeded, and never lets a
// failure here surface to the agent). Kept deliberately minimal for now -
// just name and county, in both the subject and the description.
export const createNewAgentRegisteredTicket = ({ agentData, values = {} }) => {
  const fullName =
    `${agentData.listgenUserFirstName || ""} ${agentData.listgenUserLastName || ""}`.trim() ||
    "New Agent";
  const countyID = agentData.countyID || values.countyID || "";
  const countyName = countyID ? Constants.COUNTY_ID_NAME_MAP[countyID] : "";

  return createBrokerageFreshdeskTicket({
    agentData,
    subject: `New Agent Registered - ${fullName}${countyName ? ` - ${countyName}` : ""}`,
    description: buildNewAgentRegisteredDescription(fullName, countyName),
  });
};

export const shouldCreateOfficeNotListedTicket = (previousValue, nextValue) =>
  isOfficeNotListed(nextValue) && !isOfficeNotListed(previousValue);

export const shouldCreateManagingBrokerNotListedTicket = (
  previousValue,
  nextValue
) =>
  isManagingBrokerNotListed(nextValue) &&
  !isManagingBrokerNotListed(previousValue);

export const createFreshdeskTicketTasks = ({
  agentData,
  values,
  screenName,
}) => {
  const tasks = [];
  const officeChangedToNotListed = shouldCreateOfficeNotListedTicket(
    agentData.brokerageOfficeID,
    values.brokerageOfficeID
  );
  const managingBrokerChangedToNotListed =
    shouldCreateManagingBrokerNotListedTicket(
      agentData.managingBrokerID,
      values.managingBrokerID
    );

  if (officeChangedToNotListed) {
    tasks.push(
      createBrokerageFreshdeskTicket({
        agentData,
        subject: OFFICE_NOT_LISTED_TICKET_SUBJECT,
        description: buildFreshdeskDescription(
          agentData,
          values,
          OFFICE_NOT_LISTED_LABEL,
          screenName
        ),
      })
    );
  }

  if (managingBrokerChangedToNotListed) {
    tasks.push(
      createBrokerageFreshdeskTicket({
        agentData,
        subject: MANAGING_BROKER_NOT_LISTED_TICKET_SUBJECT,
        description: buildFreshdeskDescription(
          agentData,
          values,
          MANAGING_BROKER_NOT_LISTED_LABEL,
          screenName
        ),
      })
    );
  }

  return tasks;
};
